import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Plus, Trash2, Printer, CheckCircle2, QrCode } from "lucide-react";
import { printQRLabels } from "@/lib/printHelper";
import ShareTrackingLink from "@/components/ShareTrackingLink";

export default function CreatePackage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [qrCodes, setQRCodes] = useState([]);
  const [products, setProducts] = useState([]);

  const [qrCodeId, setQRCodeId] = useState("");
  const [productId, setProductId] = useState("none");

  const [form, setForm] = useState({
    package_name: "", description: "", receiver_name: "",
    receiver_phone: "", receiver_address: "", special_instructions: "",
  });
  const [items, setItems] = useState([{ _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // {pkg, qr}

  useEffect(() => {
    api.get("/store/qr", { params: { status: "BLANK,PRODUCT_ASSIGNED" } })
      .then(r => setQRCodes(r.data))
      .catch(() => {});
    api.get("/store/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const updateItem = (idx, patch) =>
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const onProductPick = (idx, pid) => {
    const p = products.find(x => x.id === pid);
    updateItem(idx, { product_id: pid, item_name: p ? p.name : "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.package_name || !form.receiver_name || !form.receiver_phone || !form.receiver_address) {
      toast.error("Fill in package name, receiver name, phone, and address.");
      return;
    }
    if (mode === "existing" && !qrCodeId) {
      toast.error("Select a QR code.");
      return;
    }
    const cleanItems = items
      .filter(it => it.item_name?.trim())
      .map(it => ({ ...it, product_id: it.product_id || null, quantity: Number(it.quantity) || 1 }));
    if (cleanItems.length === 0) {
      toast.error("Add at least one item.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        qr_code_id: mode === "existing" ? qrCodeId : null,
        product_id: mode === "new" && productId !== "none" ? productId : null,
        ...form,
        items: cleanItems,
      };
      const { data } = await api.post("/store/packages", payload);
      // Fetch detail to get qr_image
      const detail = await api.get(`/store/packages/${data.package_id}`);
      setCreated(detail.data);
      toast.success("Package created.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div data-testid="create-package-success" className="space-y-6 max-w-3xl">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/40 grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-400" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Package Created</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Package <span className="text-cyan-300 font-mono-tech">{created.package_id}</span> is now waiting for pickup.
          </p>
        </div>
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-6 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-6 items-center">
          <div className="bg-white p-3 rounded-md justify-self-center">
            <img src={created.qr_image} alt={created.qr_code_id} className="w-44 h-44" />
          </div>
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">QR ID</div>
            <div className="font-mono-tech text-cyan-300">{created.qr_code_id}</div>
            <div className="mt-3 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">Receiver</div>
            <div className="text-slate-900 dark:text-white">{created.receiver_name}</div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">{created.receiver_phone}</div>
            <div className="text-slate-500 text-sm">{created.receiver_address}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          <Button
            data-testid="created-print-btn"
            onClick={() => printQRLabels([{
              qr_image: created.qr_image, qr_code_id: created.qr_code_id,
              store_name: created.store_name, assigned_product_name: created.items?.[0]?.item_name,
            }])}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            <Printer className="w-4 h-4 mr-2" /> Print Label
          </Button>
          <Button
            data-testid="created-view-packages-btn"
            variant="outline"
            onClick={() => navigate("/store/packages")}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            View My Packages
          </Button>
          <Button
            data-testid="created-new-btn"
            variant="outline"
            onClick={() => { setCreated(null); setForm({ package_name: "", description: "", receiver_name: "", receiver_phone: "", receiver_address: "", special_instructions: "" }); setItems([{ _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]); }}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            Create Another
          </Button>
        </div>
        <div className="max-w-xl mx-auto w-full">
          <ShareTrackingLink qrCodeId={created.qr_code_id} label="Share with Buyer" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="create-package-page" className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ Create Package</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Create a new package</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
          Use an existing QR code or generate a brand new one along with the package.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="mode-new-btn"
          onClick={() => setMode("new")}
          className={[
            "px-4 py-3 rounded-md border text-sm font-medium transition-colors text-left",
            mode === "new"
              ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-200"
              : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 hover:dark:bg-slate-900 hover:text-slate-900 hover:dark:text-white",
          ].join(" ")}
        >
          <div className="flex items-center gap-2 font-display font-semibold">
            <QrCode className="w-4 h-4" /> Generate New QR
          </div>
          <div className="text-xs mt-1 text-slate-600 dark:text-slate-400">A fresh QR will be created with this package.</div>
        </button>
        <button
          type="button"
          data-testid="mode-existing-btn"
          onClick={() => setMode("existing")}
          className={[
            "px-4 py-3 rounded-md border text-sm font-medium transition-colors text-left",
            mode === "existing"
              ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-200"
              : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 hover:dark:bg-slate-900 hover:text-slate-900 hover:dark:text-white",
          ].join(" ")}
        >
          <div className="flex items-center gap-2 font-display font-semibold">
            <Send className="w-4 h-4" /> Use Existing QR
          </div>
          <div className="text-xs mt-1 text-slate-600 dark:text-slate-400">Pick a blank or product-assigned QR you already generated.</div>
        </button>
      </div>

      <form onSubmit={onSubmit} className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-6 space-y-4">
        {mode === "existing" ? (
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Select QR Code</Label>
            <Select value={qrCodeId} onValueChange={setQRCodeId}>
              <SelectTrigger data-testid="cp-qr-select" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1.5">
                <SelectValue placeholder="Pick a QR code…" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                {qrCodes.map(q => (
                  <SelectItem key={q.qr_code_id} value={q.qr_code_id} data-testid={`cp-qr-opt-${q.qr_code_id}`}>
                    {q.qr_code_id} — {q.assigned_product_name || "Blank"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Assign Product to new QR (optional)</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="cp-product-select" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                <SelectItem value="none">None</SelectItem>
                {products.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Package Name">
            <Input data-testid="cp-pkg-name" value={form.package_name} onChange={e => setForm({ ...form, package_name: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
          </Field>
          <Field label="Receiver Name">
            <Input data-testid="cp-receiver-name" value={form.receiver_name} onChange={e => setForm({ ...form, receiver_name: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
          </Field>
          <Field label="Receiver Phone">
            <Input data-testid="cp-receiver-phone" value={form.receiver_phone} onChange={e => setForm({ ...form, receiver_phone: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
          </Field>
          <Field label="Description">
            <Input data-testid="cp-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
          </Field>
          <div className="col-span-2">
            <Field label="Receiver Address">
              <Textarea data-testid="cp-receiver-address" value={form.receiver_address} onChange={e => setForm({ ...form, receiver_address: e.target.value })} rows={2} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Special Instructions (optional)">
              <Textarea data-testid="cp-instructions" value={form.special_instructions} onChange={e => setForm({ ...form, special_instructions: e.target.value })} rows={2} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white" />
            </Field>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
          <div className="flex justify-between items-center mb-2">
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Items</Label>
            <Button type="button" data-testid="cp-add-item" size="sm" variant="outline" onClick={addItem} className="h-7 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it._uid || idx} className="grid grid-cols-12 gap-2 items-end border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-100/40 dark:bg-slate-900/40">
                <div className="col-span-12 sm:col-span-5">
                  <Label className="text-[11px] text-slate-500 mb-1 block">Product (optional)</Label>
                  <Select value={it.product_id || ""} onValueChange={(v) => onProductPick(idx, v)}>
                    <SelectTrigger data-testid={`cp-item-product-${idx}`} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                      <SelectValue placeholder="Pick…" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                      {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-7 sm:col-span-4">
                  <Label className="text-[11px] text-slate-500 mb-1 block">Item Name</Label>
                  <Input data-testid={`cp-item-name-${idx}`} value={it.item_name} onChange={e => updateItem(idx, { item_name: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <Label className="text-[11px] text-slate-500 mb-1 block">Qty</Label>
                  <Input data-testid={`cp-item-qty-${idx}`} type="number" min={1} value={it.quantity} onChange={e => updateItem(idx, { quantity: e.target.value })} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  {items.length > 1 && (
                    <button type="button" data-testid={`cp-remove-item-${idx}`} onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 h-9 w-9 grid place-items-center rounded-md hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
          <Button type="button" variant="outline" onClick={() => navigate("/store")} className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="cp-submit-btn"
            disabled={saving}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            <Send className="w-4 h-4 mr-2" /> {saving ? "Creating…" : "Create Package"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
